import unifiedLoggerClient from '@modules/eventStream/implementations/unifiedLoggerClient';
import { Typography } from '@rbx/ui';
import React, { FC, ReactElement, ReactNode, useEffect, useMemo, useState } from 'react';
import DocsAssistantEvent from '@modules/eventStream/enum/DocsAssistantEvent';
import { useLocalization, useTranslation } from '@rbx/intl';
import { TestId } from '@modules/utilities/constants/testConstants';
import {
  useProvidedConversationReducer,
  MessageRole,
  MessageHistoryItem,
  ConversationError,
  MessageRequestType,
  RequestId,
  FeedbackRating,
} from '@rbx/conv-ai-provider';
import useConversationMessageStyles from './ConversationMessage.styles';
import getMarkdownProcessor from '../markdown/markdownProcessor/getMarkdownProcessor';
import BotMessageActionBar from './BotMessageActionBar';
import ConversationErrorAlert from './ConversationErrorAlert';
import RegeneratedResponseHeader from './RegeneratedResponseHeader';
import StreamingIndicator from './StreamingIndicator';
import getMessageLogParams from '../utils/getMessageLogParams';

type ConversationMessageProps = {
  message?: MessageHistoryItem;
  rating?: FeedbackRating;
  isStreamingBotMessage: boolean;
  requestId?: RequestId;
  error?: ConversationError;
};

export type ConversationMessageByUserProps = {
  processedContent: ReactNode | null;
};

export type ConversationMessageByBotProps = {
  message?: MessageHistoryItem;
  rating?: FeedbackRating;
  processedContent?: ReactNode | null;
  streaming: boolean;
};

export const ConversationMessageByUser: FC<
  React.PropsWithChildren<ConversationMessageByUserProps>
> = ({ processedContent }) => {
  const { translate } = useTranslation();
  const {
    classes: { replyWrapper, reply, userReplyWrapper, userReply },
    cx,
  } = useConversationMessageStyles();

  return (
    <article
      aria-label={translate('Label.Prompt')}
      data-testid={TestId.MessagePrompt}
      className={cx(replyWrapper, userReplyWrapper)}>
      <Typography variant='body2' className={cx(reply, userReply)}>
        {processedContent}
      </Typography>
    </article>
  );
};
export const ConversationMessageByBot: FC<
  React.PropsWithChildren<ConversationMessageByBotProps>
> = ({ message, rating, processedContent, streaming }) => {
  const { translate } = useTranslation();
  const {
    classes: { replyWrapper, reply, botReply, soloStreamingIndicator },
    cx,
  } = useConversationMessageStyles();

  return (
    <article
      aria-label={translate('Label.Response')}
      className={replyWrapper}
      data-testid={TestId.MessageResponse}>
      <Typography variant='body2' className={cx(reply, botReply)}>
        {message?.context.requestType === MessageRequestType.RegenerationRequest && (
          <RegeneratedResponseHeader
            regeneratedPrompt={message?.context.originalPrompt || message.context.prompt}
          />
        )}
        {processedContent}
        {message ? (
          <BotMessageActionBar rating={rating} streaming={streaming} message={message} />
        ) : (
          streaming && <StreamingIndicator className={soloStreamingIndicator} maxNumDots={3} />
        )}
      </Typography>
    </article>
  );
};

const ConversationMessage: FC<React.PropsWithChildren<ConversationMessageProps>> = ({
  message,
  rating,
  isStreamingBotMessage,
  requestId,
  error,
}) => {
  const { locale } = useLocalization();
  const {
    state: { conversationId },
  } = useProvidedConversationReducer();
  const [processedContent, setProcessedContent] = useState<null | ReactElement>(null);
  const isMessageByBot = useMemo(
    () => isStreamingBotMessage || message?.role === MessageRole.Assistant,
    [message, isStreamingBotMessage],
  );

  useEffect(() => {
    if (message) {
      const processWithMarkdownProcess = async () => {
        const processed = await getMarkdownProcessor({
          onAnchorClickLoggedParams: getMessageLogParams({
            message,
            conversationId,
            locale,
          }),
        }).process(message.content || '');
        setProcessedContent(processed.result as React.ReactElement);
      };
      processWithMarkdownProcess();
    }
  }, [conversationId, requestId, message, message?.content, locale]);

  useEffect(() => {
    unifiedLoggerClient.logImpressionEvent({
      eventName: isMessageByBot
        ? DocsAssistantEvent.MessageByBot
        : DocsAssistantEvent.MessageByUser,
      parameters: getMessageLogParams({
        message,
        conversationId,
        locale,
      }),
    });
    // log only on first impression / component mount -> useEffect should have no dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <React.Fragment>
      {isMessageByBot ? (
        <ConversationMessageByBot
          message={message}
          processedContent={processedContent}
          streaming={isStreamingBotMessage}
          rating={rating}
        />
      ) : (
        <ConversationMessageByUser processedContent={processedContent} />
      )}
      {error && (
        <ConversationErrorAlert error={error} message={message} requestId={requestId} skipLogging />
      )}
    </React.Fragment>
  );
};

export default React.memo(ConversationMessage);
